import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import {
  InventoryListParams,
  LocalInventoryFilters,
  NumericFilterCondition,
  DEFAULT_NUMERIC_CONDITION,
  FilterDisplayOption,
  FilterDisplayOptions,
  DEFAULT_FILTER_DISPLAY_OPTION,
  NUMERIC_FILTER_IDS,
} from '@/types';
import { mergePersistedFilters, mergePersistedDisplayOptions } from './inventory-filter-migration';

const INVENTORY_FILTERS_STORAGE_KEY = 'inventory-filters-storage';

interface FilterMetadata {
  accountIds: string[];
  counterparties: string[];
  currencies: string[];
  exchanges: string[];
  caStatuses: string[];
  caTypes: string[];
  brokerAccountNames: string[];
  brokerSettlementCounterparties: string[];
  quantityRange: { min: number; max: number };
  settledQuantityRange: { min: number; max: number };
  quantityT1Range: { min: number; max: number };
  quantityT2Range: { min: number; max: number };
  quantityTxRange: { min: number; max: number };
  leverageRateRange: { min: number; max: number };
  valueEURRange: { min: number; max: number };
  currencyBenchmarkRange: { min: number; max: number };
  dailyPotentialRange: { min: number; max: number };
  adtv30DRange: { min: number; max: number };
  adtvMultipleRange: { min: number; max: number };
  adtvAdjustedRange: { min: number; max: number };
  benchmarkFeeRange: { min: number; max: number };
  activeUtilisationRange: { min: number; max: number };
  quantityOnLoanRange: { min: number; max: number };
  rebateRateRange: { min: number; max: number };
  l2cpCounterparties: string[];
  l2cpQuantityRange: { min: number; max: number };
  l2cpAmountEurRange: { min: number; max: number };
  l2cpRateRange: { min: number; max: number };
  l2cpGrossRateRange: { min: number; max: number };
  l2cpSnpDeltaRange: { min: number; max: number };
  pendingTransfersOutRange: { min: number; max: number };
  cimbEq2QuantityRange: { min: number; max: number };
  cimbSfQuantityRange: { min: number; max: number };
  cimbSf3QuantityRange: { min: number; max: number };
  velocityLQuantityRange: { min: number; max: number };
  velocityMQuantityRange: { min: number; max: number };
  exDateMin: string;
  exDateMax: string;
}

type FilterDisplayOptionKey = keyof FilterDisplayOption;

interface InventoryFilterStore {
  inventoryFilters: InventoryListParams;
  localInventoryFilters: LocalInventoryFilters;
  filterMetadata: FilterMetadata;
  filterDisplayOptions: FilterDisplayOptions;
  setInventoryFilters: (filters: InventoryListParams) => void;
  setLocalInventoryFilters: (filters: LocalInventoryFilters) => void;
  setFilterMetadata: (metadata: FilterMetadata) => void;
  updateFilterDisplayOption: (
    filterId: string,
    key: FilterDisplayOptionKey,
    value: boolean
  ) => void;
  clearInventoryFilters: () => void;
  clearLocalInventoryFilters: () => void;
  /** Reset a single filter by id to its default (empty) state. */
  resetLocalFilter: (filterId: string) => void;
}

const defaultCondition = (): NumericFilterCondition => ({ ...DEFAULT_NUMERIC_CONDITION });

export const defaultFilterDisplayOptions: FilterDisplayOptions = Object.fromEntries(
  NUMERIC_FILTER_IDS.map((id) => [id, { ...DEFAULT_FILTER_DISPLAY_OPTION }])
);

export const defaultLocalFilters: LocalInventoryFilters = {
  accountIds: [],
  counterparties: [],
  currencies: [],
  exchanges: [],
  caStatuses: [],
  caTypes: [],
  brokerAccountNames: [],
  brokerSettlementCounterparties: [],
  quantityCondition: defaultCondition(),
  settledQuantityCondition: defaultCondition(),
  quantityT1Condition: defaultCondition(),
  quantityT2Condition: defaultCondition(),
  quantityTxCondition: defaultCondition(),
  leverageRateCondition: defaultCondition(),
  valueEURCondition: defaultCondition(),
  currencyBenchmarkCondition: defaultCondition(),
  dailyPotentialCondition: defaultCondition(),
  adtv30DCondition: defaultCondition(),
  adtvMultipleCondition: defaultCondition(),
  adtvAdjustedCondition: defaultCondition(),
  benchmarkFeeCondition: defaultCondition(),
  activeUtilisationCondition: defaultCondition(),
  quantityOnLoanCondition: defaultCondition(),
  rebateRateCondition: defaultCondition(),
  l2cpCounterparties: [],
  l2cpQuantityCondition: defaultCondition(),
  l2cpAmountEurCondition: defaultCondition(),
  l2cpRateCondition: defaultCondition(),
  l2cpGrossRateCondition: defaultCondition(),
  l2cpSnpDeltaCondition: defaultCondition(),
  pendingTransfersOutCondition: defaultCondition(),
  cimbEq2QuantityCondition: defaultCondition(),
  cimbSfQuantityCondition: defaultCondition(),
  cimbSf3QuantityCondition: defaultCondition(),
  velocityLQuantityCondition: defaultCondition(),
  velocityMQuantityCondition: defaultCondition(),
  exDateFrom: null,
  exDateTo: null,
};

const defaultMetadata: FilterMetadata = {
  accountIds: [],
  counterparties: [],
  currencies: [],
  exchanges: [],
  caStatuses: [],
  caTypes: [],
  brokerAccountNames: [],
  brokerSettlementCounterparties: [],
  quantityRange: { min: 0, max: 1000000 },
  settledQuantityRange: { min: 0, max: 1000000 },
  quantityT1Range: { min: 0, max: 1000000 },
  quantityT2Range: { min: 0, max: 1000000 },
  quantityTxRange: { min: 0, max: 1000000 },
  leverageRateRange: { min: 0, max: 100 },
  valueEURRange: { min: 0, max: 1000000 },
  currencyBenchmarkRange: { min: 0, max: 100 },
  dailyPotentialRange: { min: 0, max: 10000 },
  adtv30DRange: { min: 0, max: 1000000 },
  adtvMultipleRange: { min: 0, max: 100 },
  adtvAdjustedRange: { min: 0, max: 10000 },
  benchmarkFeeRange: { min: 0, max: 100 },
  activeUtilisationRange: { min: 0, max: 100 },
  quantityOnLoanRange: { min: 0, max: 1000000 },
  rebateRateRange: { min: -100, max: 100 },
  l2cpCounterparties: [],
  l2cpQuantityRange: { min: 0, max: 10000000 },
  l2cpAmountEurRange: { min: 0, max: 100000000 },
  l2cpRateRange: { min: -100, max: 100 },
  l2cpGrossRateRange: { min: -100, max: 100 },
  l2cpSnpDeltaRange: { min: -100, max: 100 },
  pendingTransfersOutRange: { min: 0, max: 1000000 },
  cimbEq2QuantityRange: { min: 0, max: 1000000 },
  cimbSfQuantityRange: { min: 0, max: 1000000 },
  cimbSf3QuantityRange: { min: 0, max: 1000000 },
  velocityLQuantityRange: { min: 0, max: 1000000 },
  velocityMQuantityRange: { min: 0, max: 1000000 },
  exDateMin: '',
  exDateMax: '',
};

const FILTER_ID_TO_CONDITION_KEY: Record<string, keyof LocalInventoryFilters> = {
  quantity: 'quantityCondition',
  settledQuantity: 'settledQuantityCondition',
  quantityT1: 'quantityT1Condition',
  quantityT2: 'quantityT2Condition',
  quantityTx: 'quantityTxCondition',
  leverageRate: 'leverageRateCondition',
  valueEUR: 'valueEURCondition',
  currencyBenchmark: 'currencyBenchmarkCondition',
  dailyPotential: 'dailyPotentialCondition',
  adtv30D: 'adtv30DCondition',
  adtvMultiple: 'adtvMultipleCondition',
  adtvAdjusted: 'adtvAdjustedCondition',
  benchmarkFee: 'benchmarkFeeCondition',
  activeUtilisation: 'activeUtilisationCondition',
  quantityOnLoan: 'quantityOnLoanCondition',
  rebateRate: 'rebateRateCondition',
  pendingTransfersOut: 'pendingTransfersOutCondition',
  cimbEq2: 'cimbEq2QuantityCondition',
  cimbSf: 'cimbSfQuantityCondition',
  cimbSf3: 'cimbSf3QuantityCondition',
  velocityL: 'velocityLQuantityCondition',
  velocityM: 'velocityMQuantityCondition',
};

export const useInventoryFilterStore = create<InventoryFilterStore>()(
  devtools(
    persist(
      (set) => ({
        inventoryFilters: {},
        localInventoryFilters: defaultLocalFilters,
        filterMetadata: defaultMetadata,
        filterDisplayOptions: defaultFilterDisplayOptions,
        setInventoryFilters: (filters) => set({ inventoryFilters: filters }),
        setLocalInventoryFilters: (filters) => set({ localInventoryFilters: filters }),
        setFilterMetadata: (metadata) => set({ filterMetadata: metadata }),
        updateFilterDisplayOption: (filterId, key, value) =>
          set((state) => ({
            filterDisplayOptions: {
              ...state.filterDisplayOptions,
              [filterId]: {
                ...DEFAULT_FILTER_DISPLAY_OPTION,
                ...state.filterDisplayOptions[filterId],
                [key]: value,
              },
            },
          })),
        clearInventoryFilters: () => set({ inventoryFilters: {} }),
        clearLocalInventoryFilters: () => set({ localInventoryFilters: defaultLocalFilters, filterDisplayOptions: defaultFilterDisplayOptions }),
        resetLocalFilter: (filterId) =>
          set((state) => {
            const prev = state.localInventoryFilters;
            const next = { ...prev };
            if (filterId === 'l2cpGroup') {
              next.l2cpCounterparties = [];
              next.l2cpQuantityCondition = defaultCondition();
              next.l2cpAmountEurCondition = defaultCondition();
              next.l2cpRateCondition = defaultCondition();
              next.l2cpGrossRateCondition = defaultCondition();
              next.l2cpSnpDeltaCondition = defaultCondition();
              return { localInventoryFilters: next };
            }
            if (filterId === 'l2cpCounterparties') {
              next.l2cpCounterparties = [];
              return { localInventoryFilters: next };
            }
            if (filterId === 'l2cpQuantity') { next.l2cpQuantityCondition = defaultCondition(); return { localInventoryFilters: next }; }
            if (filterId === 'l2cpAmountEur') { next.l2cpAmountEurCondition = defaultCondition(); return { localInventoryFilters: next }; }
            if (filterId === 'l2cpRate') { next.l2cpRateCondition = defaultCondition(); return { localInventoryFilters: next }; }
            if (filterId === 'l2cpGrossRate') { next.l2cpGrossRateCondition = defaultCondition(); return { localInventoryFilters: next }; }
            if (filterId === 'l2cpSnpDelta') { next.l2cpSnpDeltaCondition = defaultCondition(); return { localInventoryFilters: next }; }
            const conditionKey = FILTER_ID_TO_CONDITION_KEY[filterId];
            if (conditionKey) {
              (next as Record<string, unknown>)[conditionKey] = defaultCondition();
              return { localInventoryFilters: next };
            }
            if (filterId === 'accountId') next.accountIds = [];
            else if (filterId === 'counterparty') next.counterparties = [];
            else if (filterId === 'currency') next.currencies = [];
            else if (filterId === 'exchange') next.exchanges = [];
            else if (filterId === 'caStatus') next.caStatuses = [];
            else if (filterId === 'caType') next.caTypes = [];
            else if (filterId === 'brokerAccountName') next.brokerAccountNames = [];
            else if (filterId === 'brokerSettlementCounterparty') next.brokerSettlementCounterparties = [];
            else if (filterId === 'exDate') {
              next.exDateFrom = null;
              next.exDateTo = null;
            }
            return { localInventoryFilters: next };
          }),
      }),
      {
        name: INVENTORY_FILTERS_STORAGE_KEY,
        version: 0,
        partialize: (state) => ({
          localInventoryFilters: state.localInventoryFilters,
          filterDisplayOptions: state.filterDisplayOptions,
          // Persist filterMetadata so that on reload the chip-collapse logic
          // ("all" / "all except") has an option pool to compare against
          // before the fresh fetch lands. Stale metadata gets overwritten by
          // setFilterMetadata once the data query resolves.
          filterMetadata: state.filterMetadata,
        }),
        merge: (persistedState: unknown, currentState: InventoryFilterStore): InventoryFilterStore => {
          const persisted =
            typeof persistedState === 'object' && persistedState !== null
              ? (persistedState as {
                  localInventoryFilters?: Partial<LocalInventoryFilters> & Record<string, unknown>;
                  filterDisplayOptions?: FilterDisplayOptions;
                  filterMetadata?: Partial<FilterMetadata>;
                })
              : undefined;
          const localInventoryFilters = mergePersistedFilters(
            persisted?.localInventoryFilters,
            currentState.localInventoryFilters,
            defaultLocalFilters
          );
          const filterDisplayOptions = mergePersistedDisplayOptions(
            persisted?.filterDisplayOptions,
            currentState.filterDisplayOptions,
            defaultFilterDisplayOptions
          );
          // Spread defaults first so any new fields added in future store
          // versions are populated even when reading older persisted state.
          const filterMetadata = persisted?.filterMetadata
            ? { ...currentState.filterMetadata, ...persisted.filterMetadata }
            : currentState.filterMetadata;
          return { ...currentState, localInventoryFilters, filterDisplayOptions, filterMetadata };
        },
      }
    ),
    { name: 'inventory-filter-store', enabled: import.meta.env.DEV }
  )
);

